import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBrandingService } from "../apis/branding/useBrandingService";
import {
  TBranding,
  TUpdateBrandingRequest,
  TUploadLogoRequest,
  TUploadAssetRequest,
} from "../apis/branding/branding.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useBrandingHook = () => {
  const { getBranding } = useBrandingService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TBranding>({
    queryKey: ["branding", selectedInstitute?.id],
    queryFn: () => getBranding(selectedInstitute?.id),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateBrandingHook = () => {
  const { updateBranding } = useBrandingService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TBranding,
    Error,
    { id: string; request: TUpdateBrandingRequest }
  >({
    mutationFn: ({ id, request }) => updateBranding(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["branding", selectedInstitute?.id],
      });
    },
  });
};

export const useUploadLogoHook = () => {
  const { uploadLogo } = useBrandingService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TBranding,
    Error,
    { request: TUploadLogoRequest; onProgress?: (progress: number) => void }
  >({
    mutationFn: ({ request, onProgress }) => uploadLogo(request, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["branding", selectedInstitute?.id],
      });
    },
  });
};

export const useUploadAssetHook = () => {
  const { uploadAsset } = useBrandingService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TBranding,
    Error,
    { request: TUploadAssetRequest; onProgress?: (progress: number) => void }
  >({
    mutationFn: ({ request, onProgress }) => uploadAsset(request, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["branding", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteAssetHook = () => {
  const { deleteAsset } = useBrandingService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TBranding, Error, { instituteId: string; assetId: string }>({
    mutationFn: ({ instituteId, assetId }) => deleteAsset(instituteId, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["branding", selectedInstitute?.id],
      });
    },
  });
};
